import React, { useEffect, useState, useRef } from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { ShieldIcon, BarChartIcon, ScrollIcon, BuildingIcon, CheckCircleIcon, TrendingUpIcon, HeadphonesIcon, AwardIcon, ArrowRightIcon, UserIcon, GraduationCapIcon, HeartIcon, FacebookIcon, InstagramIcon, ChevronDownIcon } from 'lucide-react';
const Home = () => {
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = [useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null) // CTA
  ];
  // Track visible elements for animation
  const [isVisible, setIsVisible] = useState({
    promise: false,
    whyUs: false,
    services: false,
    trust: false,
    insights: false
  });
  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      // Determine which section is currently visible
      const currentPosition = window.scrollY + window.innerHeight / 2;
      sectionRefs.forEach((ref, index) => {
        if (ref.current) {
          const {
            offsetTop,
            offsetHeight
          } = ref.current;
          if (currentPosition >= offsetTop && currentPosition < offsetTop + offsetHeight) {
            setActiveSection(index);
          }
        }
      });
      // Check visibility for animation elements
      const promiseSection = document.getElementById('promise');
      const whyUsSection = document.getElementById('why-us');
      const servicesSection = document.getElementById('services');
      const trustSection = document.getElementById('trust');
      const insightsSection = document.getElementById('insights');
      if (promiseSection) {
        const promisePosition = promiseSection.getBoundingClientRect();
        setIsVisible(prev => ({
          ...prev,
          promise: promisePosition.top < window.innerHeight * 0.75
        }));
      }
      if (whyUsSection) {
        const whyUsPosition = whyUsSection.getBoundingClientRect();
        setIsVisible(prev => ({
          ...prev,
          whyUs: whyUsPosition.top < window.innerHeight * 0.75
        }));
      }
      if (servicesSection) {
        const servicesPosition = servicesSection.getBoundingClientRect();
        setIsVisible(prev => ({
          ...prev,
          services: servicesPosition.top < window.innerHeight * 0.75
        }));
      }
      if (trustSection) {
        const trustPosition = trustSection.getBoundingClientRect();
        setIsVisible(prev => ({
          ...prev,
          trust: trustPosition.top < window.innerHeight * 0.75
        }));
      }
      if (insightsSection) {
        const insightsPosition = insightsSection.getBoundingClientRect();
        setIsVisible(prev => ({
          ...prev,
          insights: insightsPosition.top < window.innerHeight * 0.75
        }));
      }
      // Animate elements as they come into view
      const animateElements = document.querySelectorAll('.animate-on-scroll');
      animateElements.forEach(element => {
        const elementPosition = element.getBoundingClientRect();
        const isElementVisible = elementPosition.top < window.innerHeight * 0.85;
        if (isElementVisible) {
          element.classList.add('is-visible');
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    // Trigger once on mount
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Scroll to next section
  const scrollToNextSection = () => {
    const nextIndex = Math.min(activeSection + 1, sectionRefs.length - 1);
    sectionRefs[nextIndex].current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="w-full" data-id="element-359">
      {/* Hero Section */}
      <section ref={sectionRefs[0]} className="min-h-screen bg-cover bg-center flex items-center relative scroll-snap-section" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80")'
    }} data-id="element-360">
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/90 to-secondary/70" data-id="element-361"></div>
        <div className="container mx-auto px-4 relative z-10 py-20" data-id="element-362">
          <div className="max-w-3xl animate-slideUp" data-id="element-363">
            <h1 className="text-white font-bold mb-6 leading-tight" data-id="element-364">
              Magnavera Group – <br data-id="element-365" />
              <span className="text-primary" data-id="element-366">
                Navigating Wealth with Confidence
              </span>
            </h1>
            <p className="text-white text-lg md:text-xl max-w-2xl mb-10 leading-relaxed font-open-sans" data-id="element-367">
              Expert advisory services tailored to your unique needs and
              circumstances, delivered with integrity and precision.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-368">
              <Button href="/contact" className="px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-369">
                Get in Touch with Us
              </Button>
              <Button href="/about" variant="secondary" className="px-8 py-4 bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20" data-id="element-370">
                Learn More About Us
              </Button>
            </div>
          </div>
          {/* Scroll down indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-white animate-bounce cursor-pointer" onClick={scrollToNextSection} data-id="element-371">
            <span className="text-sm mb-2 font-poppins" data-id="element-372">Scroll Down</span>
            <ChevronDownIcon size={24} className="text-primary" data-id="element-373" />
          </div>
        </div>
      </section>
      {/* Our Promise Section */}
      <section ref={sectionRefs[1]} id="promise" className="min-h-screen flex items-center py-20 bg-white scroll-snap-section" data-id="element-374">
        <div className="container mx-auto px-4" data-id="element-375">
          <div className="max-w-4xl mx-auto text-center animate-on-scroll from-bottom" data-id="element-376">
            <span className="inline-block text-primary font-semibold mb-2 uppercase tracking-wider text-sm" data-id="element-377">
              Our Promise
            </span>
            <h2 className="text-center font-bold mb-6 font-raleway" data-id="element-378">
              Building Financial Freedom Together
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto mb-8" data-id="element-379"></div>
            <p className="text-text-secondary text-lg leading-relaxed mb-8 font-open-sans" data-id="element-380">
              At Magnavera Group, we believe that financial freedom is built on
              education, ethics, and empowerment. Our advisors go beyond
              transactions — we partner with you for the long term, helping you
              make confident, informed decisions at every stage of life.
            </p>
            <Button href="/about" variant="secondary" className="group" data-id="element-381">
              Learn More About Us
              <ArrowRightIcon size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" data-id="element-382" />
            </Button>
            {/* Scroll down indicator */}
            <div className="mt-16 flex flex-col items-center text-text-secondary animate-bounce cursor-pointer" onClick={scrollToNextSection} data-id="element-383">
              <span className="text-sm mb-2 font-poppins" data-id="element-384">Continue</span>
              <ChevronDownIcon size={24} className="text-primary" data-id="element-385" />
            </div>
          </div>
        </div>
      </section>
      {/* Why Us Section */}
      <section ref={sectionRefs[2]} id="why-us" className="min-h-screen flex items-center py-20 bg-secondary scroll-snap-section" data-id="element-386">
        <div className="container mx-auto px-4" data-id="element-387">
          <div className="text-center mb-16 animate-on-scroll from-bottom" data-id="element-388">
            <span className="inline-block text-primary font-semibold mb-2 uppercase tracking-wider text-sm" data-id="element-389">
              Why Choose Us
            </span>
            <h2 className="text-center font-bold mb-4 text-white" data-id="element-390">
              The Magnavera Advantage
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto" data-id="element-391"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10" data-id="element-392">
            {[{
            icon: <ShieldIcon size={32} className="text-primary" data-id="element-393" />,
            title: 'Trusted Guidance',
            description: 'Built on integrity and transparency, our advisors provide honest, straightforward guidance you can rely on.',
            delay: 0
          }, {
            icon: <AwardIcon size={32} className="text-primary" data-id="element-394" />,
            title: 'Proven Expertise',
            description: 'Our advisors are recognized for MDRT and industry excellence, bringing professional knowledge to every client.',
            delay: 100
          }, {
            icon: <GraduationCapIcon size={32} className="text-primary" data-id="element-395" />,
            title: 'Culture of Growth',
            description: 'A team environment that nurtures leaders and celebrates achievements, ensuring continuous improvement.',
            delay: 200
          }, {
            icon: <HeartIcon size={32} className="text-primary" data-id="element-396" />,
            title: 'Community Commitment',
            description: 'Actively engaged in social responsibility and peacebuilding initiatives that strengthen our communities.',
            delay: 300
          }].map((item, index) => <div key={index} className={`bg-white p-8 rounded-lg shadow-lg border border-gray-100 transition-all duration-700 transform ${isVisible.whyUs ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} animate-on-scroll from-bottom`} style={{
            transitionDelay: `${item.delay}ms`
          }} data-id="element-397">
                <div className="bg-primary/10 p-4 rounded-lg inline-block mb-6" data-id="element-398">
                  {item.icon}
                </div>
                <h3 className="font-bold mb-4 text-xl text-text-primary font-raleway" data-id="element-399">
                  {item.title}
                </h3>
                <p className="text-text-secondary font-open-sans" data-id="element-400">
                  {item.description}
                </p>
              </div>)}
          </div>
          {/* Scroll down indicator */}
          <div className="mt-16 flex flex-col items-center text-white animate-bounce cursor-pointer" onClick={scrollToNextSection} data-id="element-401">
            <span className="text-sm mb-2 font-poppins" data-id="element-402">Continue</span>
            <ChevronDownIcon size={24} className="text-primary" data-id="element-403" />
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section ref={sectionRefs[3]} className="min-h-screen flex items-center py-16 bg-gradient-to-br from-secondary to-secondary/80 text-white scroll-snap-section" data-id="element-404">
        <div className="container mx-auto px-4" data-id="element-405">
          <div className="max-w-4xl mx-auto text-center mb-16 animate-on-scroll from-bottom" data-id="element-406">
            <h2 className="text-center font-bold mb-6 text-white" data-id="element-407">
              Our Track Record
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto mb-8" data-id="element-408"></div>
            <p className="text-white/80 text-lg leading-relaxed mb-16 font-open-sans" data-id="element-409">
              With years of experience and thousands of satisfied clients, we've
              built a reputation for excellence in financial advisory services.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-410">
            {[{
            number: '15+',
            label: 'Years of Experience',
            delay: 0
          }, {
            number: '5,000+',
            label: 'Satisfied Clients',
            delay: 200
          }, {
            number: '98%',
            label: 'Client Retention',
            delay: 400
          }, {
            number: '24/7',
            label: 'Client Support',
            delay: 600
          }].map((stat, index) => <div key={index} className="text-center animate-on-scroll scale-in" style={{
            transitionDelay: `${stat.delay}ms`
          }} data-id="element-411">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2 font-raleway" data-id="element-412">
                  {stat.number}
                </div>
                <p className="text-gray-300 font-poppins uppercase tracking-wider text-sm" data-id="element-413">
                  {stat.label}
                </p>
              </div>)}
          </div>
          {/* Scroll down indicator */}
          <div className="mt-16 flex flex-col items-center text-white animate-bounce cursor-pointer" onClick={scrollToNextSection} data-id="element-414">
            <span className="text-sm mb-2 font-poppins" data-id="element-415">Continue</span>
            <ChevronDownIcon size={24} className="text-primary" data-id="element-416" />
          </div>
        </div>
      </section>
      {/* Featured Services */}
      <section ref={sectionRefs[4]} id="services" className="min-h-screen flex items-center py-20 bg-white scroll-snap-section" data-id="element-417">
        <div className="container mx-auto px-4" data-id="element-418">
          <div className="text-center mb-16 animate-on-scroll from-bottom" data-id="element-419">
            <span className="inline-block text-primary font-semibold mb-2 uppercase tracking-wider text-sm" data-id="element-420">
              Our Expertise
            </span>
            <h2 className="text-center font-bold mb-4 font-raleway" data-id="element-421">
              Premium Services
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto" data-id="element-422"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-423">
            {[{
            icon: <BarChartIcon size={32} className="text-primary" data-id="element-424" />,
            title: 'Wealth & Investment',
            description: 'Portfolio creation, SRS optimization, and family office structuring for comprehensive wealth management.',
            link: {
              text: 'Learn More',
              url: '/services'
            },
            delay: 0
          }, {
            icon: <ShieldIcon size={32} className="text-primary" data-id="element-425" />,
            title: 'Insurance & Protection',
            description: 'Life, health, legacy planning, and retirement coverage to secure your future and loved ones.',
            link: {
              text: 'Learn More',
              url: '/services'
            },
            delay: 100
          }, {
            icon: <ScrollIcon size={32} className="text-primary" data-id="element-426" />,
            title: 'Estate & Legacy',
            description: 'Wills, LPA, and multi-generational wealth planning to ensure your legacy continues as intended.',
            link: {
              text: 'Learn More',
              url: '/services'
            },
            delay: 200
          }, {
            icon: <BuildingIcon size={32} className="text-primary" data-id="element-427" />,
            title: 'Corporate Solutions',
            description: 'Employee benefits, business insurance, Work Injury Compensation, and Buy-Sell Agreements.',
            link: {
              text: 'Learn More',
              url: '/services'
            },
            delay: 300
          }].map((service, index) => <div key={index} className={`transition-all duration-700 transform ${isVisible.services ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} animate-on-scroll from-bottom`} style={{
            transitionDelay: `${service.delay}ms`
          }} data-id="element-428">
                <Card title={service.title} icon={service.icon} link={service.link} className="h-full hover:shadow-lg transition-shadow duration-300" data-id="element-429">
                  {service.description}
                </Card>
              </div>)}
          </div>
          <div className="mt-12 text-center" data-id="element-430">
            <Button href="/services" variant="secondary" className="group" data-id="element-431">
              View All Services
              <ArrowRightIcon size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" data-id="element-432" />
            </Button>
          </div>
          {/* Scroll down indicator */}
          <div className="mt-16 flex flex-col items-center text-text-secondary animate-bounce cursor-pointer" onClick={scrollToNextSection} data-id="element-433">
            <span className="text-sm mb-2 font-poppins" data-id="element-434">Continue</span>
            <ChevronDownIcon size={24} className="text-primary" data-id="element-435" />
          </div>
        </div>
      </section>
      {/* Featured Case Study */}
      <section ref={sectionRefs[5]} className="min-h-screen flex items-center py-20 bg-gray-50 scroll-snap-section" data-id="element-436">
        <div className="container mx-auto px-4" data-id="element-437">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-438">
            <div className="animate-on-scroll from-left" data-id="element-439">
              <span className="inline-block text-primary font-semibold mb-2 uppercase tracking-wider text-sm" data-id="element-440">
                Our Approach
              </span>
              <h2 className="font-bold mb-6 font-raleway" data-id="element-441">
                Strategic Advisory Process
              </h2>
              <p className="text-text-secondary mb-6 leading-relaxed font-open-sans" data-id="element-442">
                At Magnavera Group, we believe in a comprehensive approach to
                advisory services. Our process is designed to understand your
                unique situation, identify potential risks, and develop tailored
                solutions that provide optimal protection.
              </p>
              <div className="space-y-4 mb-8" data-id="element-443">
                {['Thorough needs assessment and risk analysis', 'Customized solution development', 'Regular portfolio reviews and adjustments', 'Proactive communication and support'].map((item, index) => <div key={index} className="flex items-start" data-id="element-444">
                    <div className="bg-primary/10 p-1 rounded-full text-primary mr-3 mt-1" data-id="element-445">
                      <CheckCircleIcon size={18} data-id="element-446" />
                    </div>
                    <span className="font-open-sans" data-id="element-447">{item}</span>
                  </div>)}
              </div>
              <Button href="/contact" data-id="element-448">Schedule a Consultation</Button>
            </div>
            <div className="relative animate-on-scroll from-right" data-id="element-449">
              <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80" alt="Strategic Advisory Process" className="rounded-lg shadow-xl w-full" data-id="element-450" />
              <div className="absolute -bottom-6 -left-6 bg-primary text-secondary p-6 rounded-lg shadow-lg hidden md:block" data-id="element-451">
                <div className="text-3xl font-bold mb-1 font-raleway" data-id="element-452">98%</div>
                <div className="text-sm font-medium font-poppins" data-id="element-453">
                  Client Satisfaction
                </div>
              </div>
            </div>
          </div>
          {/* Scroll down indicator */}
          <div className="mt-16 flex flex-col items-center text-text-secondary animate-bounce cursor-pointer" onClick={scrollToNextSection} data-id="element-454">
            <span className="text-sm mb-2 font-poppins" data-id="element-455">Continue</span>
            <ChevronDownIcon size={24} className="text-primary" data-id="element-456" />
          </div>
        </div>
      </section>
      {/* Featured Insights */}
      <section ref={sectionRefs[6]} id="insights" className="min-h-screen flex items-center py-20 bg-white scroll-snap-section" data-id="element-457">
        <div className="container mx-auto px-4" data-id="element-458">
          <div className="text-center mb-16 animate-on-scroll from-bottom" data-id="element-459">
            <span className="inline-block text-primary font-semibold mb-2 uppercase tracking-wider text-sm" data-id="element-460">
              Stay Informed
            </span>
            <h2 className="text-center font-bold mb-4 font-raleway" data-id="element-461">
              Featured Insights & Events
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto" data-id="element-462"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-463">
            {[{
            platform: 'Facebook',
            icon: <FacebookIcon size={24} className="text-[#1877F2]" data-id="element-464" />,
            title: 'Financial Planning Workshop',
            date: 'June 15, 2023',
            excerpt: 'Join us for an interactive session on essential financial planning strategies for young professionals.',
            imageUrl: 'https://images.unsplash.com/photo-1591115765373-5207764f72e4?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
            delay: 0
          }, {
            platform: 'Instagram',
            icon: <InstagramIcon size={24} className="text-[#E1306C]" data-id="element-465" />,
            title: 'Client Appreciation Day',
            date: 'July 8, 2023',
            excerpt: 'Thank you to all our amazing clients who joined us for our annual appreciation event!',
            imageUrl: 'https://images.unsplash.com/photo-1511795409834-432f7b1d6574?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
            delay: 100
          }, {
            platform: 'Facebook',
            icon: <FacebookIcon size={24} className="text-[#1877F2]" data-id="element-466" />,
            title: 'Retirement Planning Seminar',
            date: 'August 22, 2023',
            excerpt: 'Discover strategies to ensure a comfortable retirement with our expert advisors.',
            imageUrl: 'https://images.unsplash.com/photo-1532102235608-dc8fc689c9ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
            delay: 200
          }].map((post, index) => <div key={index} className={`bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100 transition-all duration-700 transform ${isVisible.insights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} hover:shadow-card-hover hover:border-primary/20 animate-on-scroll from-bottom`} style={{
            transitionDelay: `${post.delay}ms`
          }} data-id="element-467">
                <img src={post.imageUrl} alt={post.title} className="w-full h-48 object-cover" data-id="element-468" />
                <div className="p-6" data-id="element-469">
                  <div className="flex items-center mb-3" data-id="element-470">
                    {post.icon}
                    <span className="text-sm text-text-secondary ml-2 font-open-sans" data-id="element-471">
                      {post.platform}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto font-open-sans" data-id="element-472">
                      {post.date}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg mb-2 font-raleway" data-id="element-473">
                    {post.title}
                  </h3>
                  <p className="text-text-secondary mb-4 font-open-sans" data-id="element-474">
                    {post.excerpt}
                  </p>
                  <a href="#" className="text-primary font-medium inline-flex items-center hover:underline" data-id="element-475">
                    Read More
                    <ArrowRightIcon size={16} className="ml-1" data-id="element-476" />
                  </a>
                </div>
              </div>)}
          </div>
          <div className="text-center mt-10" data-id="element-477">
            <Button href="#" variant="secondary" className="group" data-id="element-478">
              View All Insights
              <ArrowRightIcon size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" data-id="element-479" />
            </Button>
          </div>
          {/* Scroll down indicator */}
          <div className="mt-16 flex flex-col items-center text-text-secondary animate-bounce cursor-pointer" onClick={scrollToNextSection} data-id="element-480">
            <span className="text-sm mb-2 font-poppins" data-id="element-481">Continue</span>
            <ChevronDownIcon size={24} className="text-primary" data-id="element-482" />
          </div>
        </div>
      </section>
      {/* CTA Banner */}
      <section ref={sectionRefs[7]} className="min-h-screen flex items-center relative py-20 overflow-hidden scroll-snap-section" data-id="element-483">
        <div className="absolute inset-0 bg-secondary" data-id="element-484"></div>
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"
      }} data-id="element-485"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-486">
          <div className="max-w-3xl mx-auto text-center animate-on-scroll from-bottom" data-id="element-487">
            <h2 className="text-white font-bold mb-6 font-raleway" data-id="element-488">
              Ready to Secure Your Financial Future?
            </h2>
            <p className="text-white opacity-90 max-w-2xl mx-auto mb-10 text-lg font-open-sans" data-id="element-489">
              Contact us today for a personalized consultation. Our expert
              advisors are ready to help you navigate your financial journey
              with confidence.
            </p>
            <Button href="/contact" variant="primary" className="px-8 py-4 shadow-lg" data-id="element-490">
              Get in Touch with Us
            </Button>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;